import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, Input } from 'antd'

import {
  DatabaseOutlined,
  DownOutlined,
  FileOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  TableOutlined
} from '@ant-design/icons'
import classNames from 'classnames'

import { selectDataSources } from '@/store/slices/data/selectors'
import { selectCurrentProjectId } from '@/store/slices/projects/selectors'

import { DataSourceDto } from '@/interfaces/data'

import EmptyDataSourceUploader from '@components/imagemap/right-panel/data-source/EmptyDataSourceUploader'

import { useLocalStorage } from '@/hooks'

type Props = {
  dataTableId: number
  onSelectTable: (tableId: number) => void
}

type LastTable = {
  dataSourceId: number
  sectionId: number
  tableId: number
}

const findPath = (dataSources: DataSourceDto[], tableId: number): LastTable => {
  for (const source of dataSources) {
    for (const section of source.dataSections || []) {
      if (section.dataTables?.some(table => table.id === tableId)) {
        return { dataSourceId: source.id, sectionId: section.id, tableId }
      }
    }
  }
  return null
}

const sourceKey = (id: number) => `source-${id}`
const sectionKey = (id: number) => `section-${id}`

const DataTree = ({ dataTableId, onSelectTable }: Props) => {
  const projectId = useSelector(selectCurrentProjectId)
  const dataSources = useSelector(selectDataSources)
  const [lastTable, setLastTable] = useLocalStorage<LastTable>(projectId.toString(), null)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [query, setQuery] = useState('')
  const [searchCollapsed, setSearchCollapsed] = useState<Set<string>>(() => new Set())
  const selectedRef = useRef<HTMLDivElement>(null)
  const scrollPending = useRef(true)

  const expandPath = (path: LastTable) => {
    if (path) {
      setExpanded(
        prev =>
          new Set([...Array.from(prev), sourceKey(path.dataSourceId), sectionKey(path.sectionId)])
      )
    }
  }

  useEffect(() => {
    if (dataTableId != null) {
      expandPath(findPath(dataSources, dataTableId))
    }
  }, [dataTableId, dataSources])

  useEffect(() => {
    scrollPending.current = true
  }, [dataTableId])

  useEffect(() => {
    if (scrollPending.current && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' })
      scrollPending.current = false
    }
  })

  useEffect(() => setSearchCollapsed(new Set()), [query])

  const search = query.trim().toLowerCase()

  const visibleSources = useMemo(() => {
    const numbered = dataSources.map((source, index) => ({
      ...source,
      label: `${index + 1}. ${source.filename}`,
      dataSections: source.dataSections || []
    }))
    if (!search) {
      return numbered
    }
    return numbered
      .map(source => ({
        ...source,
        dataSections: source.dataSections
          .map(section =>
            section.name.toLowerCase().includes(search)
              ? section
              : {
                  ...section,
                  dataTables: section.dataTables.filter(table =>
                    table.name.toLowerCase().includes(search)
                  )
                }
          )
          .filter(section => section.dataTables.length > 0)
      }))
      .filter(source => source.dataSections.length > 0)
  }, [dataSources, search])

  const isOpen = (key: string) => (search ? !searchCollapsed.has(key) : expanded.has(key))

  const toggle = (key: string) => {
    const update = (prev: Set<string>) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    }
    search ? setSearchCollapsed(update) : setExpanded(update)
  }

  const selectTable = (path: LastTable) => {
    onSelectTable(path.tableId)
    setLastTable(path)
  }

  const reloadLastTable = () => {
    if (lastTable) {
      setQuery('')
      expandPath(lastTable)
      onSelectTable(lastTable.tableId)
    }
  }

  const caret = (open: boolean) => (
    <span className="node-caret">{open ? <DownOutlined /> : <RightOutlined />}</span>
  )

  return (
    <aside className="workbench-card data-tree">
      <div className="data-tree-head">
        <h4>Data</h4>
        <span className="muted">
          {dataSources.length} {dataSources.length === 1 ? 'source' : 'sources'}
        </span>
      </div>

      {dataSources.length === 0 ? (
        <div className="data-tree-nodes">
          <EmptyDataSourceUploader onClose={() => {}} />
        </div>
      ) : (
        <>
          <Input
            className="data-tree-search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search tables"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="data-tree-nodes">
            {visibleSources.map(source => {
              const sourceOpen = isOpen(sourceKey(source.id))
              return (
                <React.Fragment key={source.id}>
                  <div className="node" onClick={() => toggle(sourceKey(source.id))}>
                    {caret(sourceOpen)}
                    <DatabaseOutlined className="node-icon" />
                    <span className="node-label" title={source.label}>
                      {source.label}
                    </span>
                  </div>
                  {sourceOpen &&
                    source.dataSections.map(section => {
                      const sectionOpen = isOpen(sectionKey(section.id))
                      return (
                        <React.Fragment key={section.id}>
                          <div
                            className="node depth-1"
                            onClick={() => toggle(sectionKey(section.id))}
                          >
                            {caret(sectionOpen)}
                            <FileOutlined className="node-icon" />
                            <span className="node-label" title={section.name}>
                              {section.name}
                            </span>
                            <span className="node-meta">{section.dataTables.length}</span>
                          </div>
                          {sectionOpen &&
                            section.dataTables.map(table => {
                              const selected = table.id === dataTableId
                              return (
                                <div
                                  key={table.id}
                                  ref={selected ? selectedRef : undefined}
                                  className={classNames('node depth-2', { selected })}
                                  onClick={() =>
                                    selectTable({
                                      dataSourceId: source.id,
                                      sectionId: section.id,
                                      tableId: table.id
                                    })
                                  }
                                >
                                  <TableOutlined className="node-icon" />
                                  <span className="node-label" title={table.name}>
                                    {table.name}
                                  </span>
                                </div>
                              )
                            })}
                        </React.Fragment>
                      )
                    })}
                </React.Fragment>
              )
            })}
            {search && visibleSources.length === 0 && (
              <div className="data-tree-empty">No tables match “{query.trim()}”.</div>
            )}
          </div>
        </>
      )}

      <div className="data-tree-foot">
        <Button
          type="text"
          icon={<ReloadOutlined />}
          disabled={!lastTable}
          onClick={reloadLastTable}
        >
          Reload last table
        </Button>
      </div>
    </aside>
  )
}

export default DataTree
